import {
  EMPLOYMENT_TYPE_LABEL_VI,
  VENUE_TYPE_LABEL_VI,
  WORKER_POSITION_LABEL_VI,
  type EmploymentType,
  type SalaryType,
  type VenueType,
  type WorkerPosition,
} from "@chotca/config";
import { ChotcaApiError } from "@chotca/sdk";
import type { useToast } from "@chotca/ui";

// ── Salary VND formatting ──────────────────────────────────────────────────
const VND_GROUP = /\B(?=(\d{3})+(?!\d))/g;

export function formatVnd(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  const n =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/\D/g, ""));
  if (!Number.isFinite(n) || n === 0) return "";
  return String(Math.trunc(n)).replace(VND_GROUP, ".");
}

export function parseVnd(input: string): number {
  const digits = input.replace(/\D/g, "");
  return digits === "" ? 0 : Number(digits);
}

// ── Salary presets per unit ────────────────────────────────────────────────
export interface SalaryPreset {
  label: string;
  min: number;
  max: number;
}

export const SALARY_PRESETS: Record<SalaryType, readonly SalaryPreset[]> = {
  PER_MONTH: [
    { label: "5–7tr", min: 5_000_000, max: 7_000_000 },
    { label: "7–10tr", min: 7_000_000, max: 10_000_000 },
    { label: "10–15tr", min: 10_000_000, max: 15_000_000 },
    { label: "15–20tr", min: 15_000_000, max: 20_000_000 },
  ],
  PER_DAY: [
    { label: "200–250k", min: 200_000, max: 250_000 },
    { label: "250–350k", min: 250_000, max: 350_000 },
    { label: "350–500k", min: 350_000, max: 500_000 },
  ],
  PER_SHIFT: [
    { label: "150–200k", min: 150_000, max: 200_000 },
    { label: "200–300k", min: 200_000, max: 300_000 },
    { label: "300–400k", min: 300_000, max: 400_000 },
  ],
  PER_HOUR: [
    { label: "22–25k", min: 22_000, max: 25_000 },
    { label: "25–30k", min: 25_000, max: 30_000 },
    { label: "30–40k", min: 30_000, max: 40_000 },
  ],
};

// ── Salary unit sanity check ───────────────────────────────────────────────
// Realistic F&B pay bands per unit (VND). A value far outside its unit's band
// is almost always a unit mix-up — e.g. "10tr/giờ" (1000× too high) or
// "10k/tháng" (100× too low) — both very easy to fat-finger because the amount
// and the unit are independent inputs. We surface a soft, dismissible warning
// and, when we can, offer a one-tap fix; never a hard block, since rare edge
// cases exist (e.g. a genuinely premium hourly rate).
const SALARY_UNIT_NOUN: Record<SalaryType, string> = {
  PER_HOUR: "giờ",
  PER_SHIFT: "ca",
  PER_DAY: "ngày",
  PER_MONTH: "tháng",
};

const SALARY_REALISTIC: Record<SalaryType, { low: number; high: number }> = {
  PER_HOUR: { low: 10_000, high: 200_000 },
  PER_SHIFT: { low: 50_000, high: 1_000_000 },
  PER_DAY: { low: 80_000, high: 2_000_000 },
  PER_MONTH: { low: 1_500_000, high: 200_000_000 },
};

export interface SalarySanityWarning {
  message: string;
  /** Unit the amount would be realistic under, if we can infer one. */
  suggestedType: SalaryType | null;
  /** Display label for `suggestedType`, e.g. "/tháng". */
  suggestedLabel: string | null;
}

/**
 * Flag a salary amount that looks abnormal for its selected unit. Returns null
 * when the figure is plausible (or empty / "Thoả thuận"). When abnormal, tries
 * to infer the unit the user likely meant so the UI can offer a one-tap fix.
 */
export function checkSalarySanity(
  salaryMin: number,
  salaryMax: number,
  type: SalaryType,
): SalarySanityWarning | null {
  const hi = Math.max(salaryMin, salaryMax);
  if (hi <= 0) return null; // empty or "Thoả thuận" (both 0)
  const lo = salaryMin > 0 ? salaryMin : salaryMax;

  const band = SALARY_REALISTIC[type];
  let amount: number;
  let tooHigh: boolean;
  if (hi > band.high) {
    amount = hi;
    tooHigh = true;
  } else if (lo < band.low) {
    amount = lo;
    tooHigh = false;
  } else {
    return null; // within the realistic band for this unit
  }

  // Which other unit would make this amount realistic? Among bands that contain
  // the amount, pick the one whose geometric center is closest (scale-aware, so
  // a 1000× gap maps cleanly: 10tr → /tháng, 10k → /giờ).
  let suggestedType: SalaryType | null = null;
  let bestDist = Infinity;
  for (const t of Object.keys(SALARY_REALISTIC) as SalaryType[]) {
    if (t === type) continue;
    const b = SALARY_REALISTIC[t];
    if (amount < b.low || amount > b.high) continue;
    const dist = Math.abs(
      Math.log(amount) - Math.log(Math.sqrt(b.low * b.high)),
    );
    if (dist < bestDist) {
      bestDist = dist;
      suggestedType = t;
    }
  }

  const noun = SALARY_UNIT_NOUN[type];
  const lead = `${formatVnd(amount)}đ/${noun} ${
    tooHigh ? "cao bất thường" : "thấp bất thường"
  } so với mức lương /${noun} thường gặp.`;

  let suggestedLabel: string | null = null;
  let tail: string;
  if (suggestedType) {
    suggestedLabel = `/${SALARY_UNIT_NOUN[suggestedType]}`;
    tail = ` Có thể bạn đang muốn chọn đơn vị ${suggestedLabel}?`;
  } else {
    tail = tooHigh
      ? " Kiểm tra lại số tiền hoặc đơn vị đã chọn."
      : " Kiểm tra xem có nhập thiếu số 0 hoặc nhầm đơn vị không.";
  }

  return { message: lead + tail, suggestedType, suggestedLabel };
}

// ── Title auto-suggest ─────────────────────────────────────────────────────
const EMP_SHORT: Record<EmploymentType, string> = {
  FULL_TIME: "toàn thời gian",
  PART_TIME: "bán thời gian",
  CASUAL: "ca lẻ",
  SEASONAL: "thời vụ",
};

export function suggestTitle(input: {
  position: WorkerPosition;
  venueType: VenueType;
  employmentType: EmploymentType;
}): string {
  const pos =
    WORKER_POSITION_LABEL_VI[input.position]?.toLowerCase() ?? "nhân viên";
  const venue = VENUE_TYPE_LABEL_VI[input.venueType]?.toLowerCase() ?? "quán";
  const emp =
    EMP_SHORT[input.employmentType] ??
    EMPLOYMENT_TYPE_LABEL_VI[input.employmentType];
  return `Tuyển ${pos} ${emp} — ${venue}`;
}

// ── Description scaffold ───────────────────────────────────────────────────
export const DESCRIPTION_SCAFFOLD =
  "Công việc:\n- \n\nThời gian:\n- \n\nYêu cầu thêm:\n- ";

// ── Error helpers ──────────────────────────────────────────────────────────
export function collectErrorMessages(
  errors: Record<string, unknown>,
  prefix = "",
): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(errors)) {
    if (!v) continue;
    if (typeof v === "object") {
      const node = v as { message?: string };
      if (typeof node.message === "string") {
        out.push(`${prefix}${k}: ${node.message}`);
      } else {
        out.push(
          ...collectErrorMessages(v as Record<string, unknown>, `${k}.`),
        );
      }
    }
  }
  return out;
}

export function firstErrorKey(errors: Record<string, unknown>): string | null {
  for (const [k, v] of Object.entries(errors)) {
    if (v) return k;
  }
  return null;
}

export function reportSubmitError(
  err: unknown,
  toast: ReturnType<typeof useToast>["toast"],
) {
  let title = "Lỗi đăng tin";
  let description = "Có lỗi không xác định. Hãy thử lại.";

  if (err instanceof ChotcaApiError) {
    title = `[${err.code}] ${err.message || "Lỗi từ máy chủ"}`;
    const hints: string[] = [];
    if (err.requestId) hints.push(`requestId=${err.requestId}`);
    if (err.details) {
      const issues = (
        err.details as {
          issues?: Array<{ path?: unknown[]; message?: string }>;
        }
      ).issues;
      if (Array.isArray(issues)) {
        const fieldErrs = issues.map((i) =>
          `${(i.path ?? []).join(".") || "?"}: ${i.message ?? ""}`.trim(),
        );
        if (fieldErrs.length > 0) hints.push(fieldErrs.slice(0, 3).join(" · "));
      }
    }
    description = hints.join(" · ") || `HTTP ${err.status}`;
  } else if (err instanceof Error) {
    title = err.name === "Error" ? "Lỗi mạng" : err.name;
    description = err.message || "Không có thông tin chi tiết";
  }

  toast({ tone: "error", title, description });
  console.error("[posting-form] submit failed:", err);
}
